import { useProfile } from "@shared/hooks/useProfile";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../../../config/firebase";
import { GroupService } from "../services/group.service";
import { GroupConversation } from "../types";
import { useLastMessages } from "./useLastMessages";

/**
 * Custom hook to manage group conversations.
 * Only handles conversations of type "group".
 */
export const useGroupConversations = () => {
  const [groups, setGroups] = useState<GroupConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useProfile();

  useEffect(() => {
    if (!user?.uid) return;

    // Query only group conversations where the user is a participant
    const q = query(
      collection(db, "conversations"),
      where("participants", "array-contains", user.uid),
      where("type", "==", "group")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newGroups = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            participants: data.participants,
            lastMessage: data.lastMessage,
            unreadCount: data.unreadCount || 0,
            title: data.title,
            color: data.color,
            type: data.type,
            createdBy: data.createdBy,
          } as GroupConversation;
        });
        setGroups(newGroups);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Enhance groups with last message and unread count
  const lastMessages = useLastMessages();
  const enhancedGroups = groups.map(group => ({
    ...group,
    lastMessage: lastMessages[group.id]?.lastMessage || null,
    unreadCount: lastMessages[group.id]?.unreadCount || 0
  }));

  /**
   * Creates a new group conversation in Firestore.
   * @param title Group name/title
   * @param participants Array of user IDs (including the creator)
   * @param color Hex color for the group
   * @returns The new group conversation ID
   */
  const createGroupConversation = async (
    title: string,
    participants: string[],
    color: string
  ): Promise<string | null> => {
    if (!user?.uid) return null;
    try {
      // Call the backend to create the group
      const groupId = await GroupService.createGroup({ title, participants, color });
      return groupId;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };


  return {
    groups: enhancedGroups,
    loading,
    error,
    createGroupConversation,
  };
};
